import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Monitor, Save, Trash2, ToggleLeft, ToggleRight, ExternalLink, Upload, Code, Loader2, Image, FolderOpen, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import adminApi from "@/lib/adminApi";

export const DesktopBannerTab = ({ onRefresh }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    ad_type: "image",
    image_url: "",
    video_url: "",
    html_url: "",
    link_url: "",
    is_active: true,
    order: 0
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const res = await adminApi.get("/desktop-banners");
      setBanners(res.data);
    } catch (error) {
      console.error("Error fetching desktop banners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const hasHtml = files.some(f => f.name.endsWith('.html') || (f.webkitRelativePath && f.webkitRelativePath.endsWith('.html')));
    if (!hasHtml) {
      toast.error("Se requiere al menos un archivo .html");
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    const pathsList = [];
    
    files.forEach(file => {
      const relativePath = file.webkitRelativePath || file.name;
      const pathParts = relativePath.split('/');
      const cleanPath = pathParts.length > 1 ? pathParts.slice(1).join('/') : relativePath;
      
      formDataUpload.append('files', file);
      pathsList.push(cleanPath);
    });
    
    formDataUpload.append('paths', JSON.stringify(pathsList));

    try {
      const res = await adminApi.post("/banner-uploads/html5", formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          html_url: res.data.html_url
        }));
        setUploadedFiles(res.data.files || []);
        toast.success(`Se subieron ${res.data.files.length} archivos`);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("Error al subir archivos");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error("Por favor selecciona un archivo de video válido");
      return;
    }

    setUploadingVideo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('folder', 'banner-videos');

    try {
      const res = await adminApi.post("/storage/upload", formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.url) {
        setFormData(prev => ({ ...prev, video_url: res.data.url }));
        toast.success("Video subido correctamente");
      }
    } catch (error) {
      console.error("Error uploading video:", error);
      toast.error("Error al subir el video");
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.ad_type === "image" && !formData.image_url.trim()) {
      toast.error("La URL de la imagen es requerida");
      return;
    }

    if (formData.ad_type === "video" && !formData.video_url.trim()) {
      toast.error("Debes subir un video o ingresar la URL");
      return;
    }
    
    if (formData.ad_type === "html5" && !formData.html_url.trim()) {
      toast.error("Debes subir los archivos HTML5");
      return;
    }

    setSaving(true);
    try {
      if (editingBanner) {
        await adminApi.put(`/desktop-banners/${editingBanner.id}`, formData);
        toast.success("Banner actualizado");
      } else {
        await adminApi.post("/desktop-banners", formData);
        toast.success("Banner creado");
      }
      resetForm();
      fetchBanners();
      onRefresh?.();
    } catch (error) {
      toast.error("Error al guardar el banner");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || "",
      ad_type: banner.ad_type || "image",
      image_url: banner.image_url || "",
      html_url: banner.html_url || "",
      link_url: banner.link_url || "",
      is_active: banner.is_active ?? true,
      order: banner.order || 0
    });
    setUploadedFiles([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este banner?")) return;
    
    try {
      await adminApi.delete(`/desktop-banners/${id}`);
      toast.success("Banner eliminado");
      fetchBanners();
      onRefresh?.();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await adminApi.put(`/desktop-banners/${banner.id}`, {
        is_active: !banner.is_active
      });
      toast.success(banner.is_active ? "Banner desactivado" : "Banner activado");
      fetchBanners();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const resetForm = () => {
    setEditingBanner(null);
    setFormData({
      title: "",
      ad_type: "image",
      image_url: "",
      video_url: "",
      html_url: "",
      link_url: "",
      is_active: true,
      order: 0
    });
    setUploadedFiles([]);
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Banners Desktop</h2>
          <p className="text-sm text-gray-500">Banners horizontales que aparecen en la página de video (solo desktop)</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título (interno) *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ej: Promo Navidad"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Banner *
            </label>
            <select
              value={formData.ad_type}
              onChange={(e) => setFormData({ ...formData, ad_type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            >
              <option value="image">Imagen (PNG/JPG/GIF)</option>
              <option value="video">Video (MP4)</option>
              <option value="html5">HTML5 Canvas</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Orden
            </label>
            <input
              type="number"
              min="0"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Image Fields */}
        {formData.ad_type === "image" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL de la Imagen (formato wide recomendado) *
            </label>
            <input
              type="text"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://ejemplo.com/banner-wide.jpg"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Tamaño sugerido: <strong>1200 x 150 px</strong> (ratio 8:1)
            </p>
          </div>
        )}

        {/* Video Fields */}
        {formData.ad_type === "video" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video MP4 *
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/webm"
                  onChange={handleVideoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="default"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploadingVideo}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
                  {uploadingVideo ? "Subiendo..." : "Subir Video"}
                </Button>
                <span className="text-sm text-gray-500">o</span>
                <input
                  type="text"
                  placeholder="URL del video"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tamaño sugerido: <strong>1200 x 150 px</strong> | Formato: MP4 o WebM | Se reproducirá en loop sin sonido
              </p>
            </div>
            {formData.video_url && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Vista previa:</p>
                <video src={formData.video_url} className="w-full max-w-md rounded border" autoPlay loop muted playsInline />
              </div>
            )}
          </div>
        )}

        {/* HTML5 Fields */}
        {formData.ad_type === "html5" && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Archivos HTML5 Canvas *
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory=""
                  directory=""
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="default"
                  onClick={() => folderInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FolderOpen className="w-4 h-4" />
                  )}
                  {uploading ? "Subiendo..." : "Seleccionar Carpeta"}
                </Button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".html,.js,.css,.png,.jpg,.jpeg,.gif,.webp,.svg,.json,.woff,.woff2,.ttf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Archivos sueltos
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tamaño del banner: <strong>1200 x 150 px</strong>
              </p>
              <p className="text-xs text-blue-600 mt-1">
                💡 <strong>Recomendado:</strong> Selecciona la carpeta exportada de Adobe Animate (incluye .html, .js e images/)
              </p>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800 mb-2">Archivos subidos:</p>
                <ul className="text-sm text-green-700 space-y-1">
                  {uploadedFiles.map((file, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Code className="w-3 h-3" />
                      {file}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {formData.html_url && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL del HTML (auto-generada)
                </label>
                <input
                  type="text"
                  value={formData.html_url}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL de Destino (al hacer clic)
          </label>
          <input
            type="text"
            value={formData.link_url}
            onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
            placeholder="https://tienda.ejemplo.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
        </div>

        {/* Preview */}
        {formData.ad_type === "image" && formData.image_url && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vista previa
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
              <img 
                src={formData.image_url} 
                alt="Preview" 
                className="w-full h-auto max-h-32 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
            />
            <span className="text-sm font-medium text-gray-700">Activo</span>
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving || uploading} className="bg-pink-600 hover:bg-pink-700">
            <Save size={18} className="mr-2" />
            {editingBanner ? "Actualizar" : "Crear"} Banner
          </Button>
          {editingBanner && (
            <Button type="button" variant="outline" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>

      {/* List */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-800">Banners en el pool</h3>
        
        {banners.length === 0 ? (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
            <Monitor size={48} className="mx-auto mb-3 opacity-30" />
            <p>No hay banners configurados</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {banners.map((banner) => (
              <div 
                key={banner.id}
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  banner.is_active 
                    ? "bg-white border-green-200" 
                    : "bg-gray-100 border-gray-200 opacity-60"
                }`}
              >
                <div className="w-32 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {banner.ad_type === "html5" ? (
                    <div className="text-center">
                      <Code className="w-6 h-6 text-purple-500 mx-auto" />
                      <span className="text-xs text-gray-500">HTML5</span>
                    </div>
                  ) : banner.image_url ? (
                    <img 
                      src={banner.image_url} 
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Image className="w-6 h-6 text-gray-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900 truncate">{banner.title}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      banner.ad_type === "html5" 
                        ? "bg-purple-100 text-purple-700"
                        : "bg-blue-100 text-blue-700"
                    }`}>
                      {banner.ad_type === "html5" ? "HTML5" : "Imagen"}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      banner.is_active 
                        ? "bg-green-100 text-green-700" 
                        : "bg-gray-200 text-gray-600"
                    }`}>
                      {banner.is_active ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                  {banner.link_url && (
                    <a 
                      href={banner.link_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate"
                    >
                      <ExternalLink size={12} />
                      {banner.link_url}
                    </a>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleActive(banner)}
                    className={banner.is_active ? "text-green-600" : "text-gray-400"}
                  >
                    {banner.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(banner)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(banner.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
        <strong>¿Cómo funciona?</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>Los banners solo aparecen en <strong>desktop</strong></li>
          <li>Se muestran <strong>debajo del video</strong> en la página de video</li>
          <li>Se elige un banner <strong>aleatorio</strong> del pool de activos</li>
          <li>Tamaño recomendado: <strong>1200 x 150 px</strong></li>
        </ul>
      </div>
    </div>
  );
};
